import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Badge, Modal, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import distributorService from '../../../../services/DistributorService';

const PortalLoginConfig = () => {
  const [configurations, setConfigurations] = useState([]);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [configData, setConfigData] = useState({
    operator: 'jio',
    username: '',
    password: '',
    status: 'inactive'
  });

  // Ref to prevent duplicate calls in StrictMode
  const hasInitialized = useRef(false);

  // Fetch portal configs on component mount
  useEffect(() => {
    if (!hasInitialized.current) {
      hasInitialized.current = true;
      fetchPortalConfigs();
    }
  }, []);

  const fetchPortalConfigs = async () => {
    setLoading(true);
    try {
      const response = await distributorService.getPortalConfigs();
      if (response.success && response.data) {
        setConfigurations(response.data.results || []);
      } else {
        toast.error(response.error || 'Failed to fetch portal configurations');
      }
    } catch (error) {
      console.error('Error fetching portal configs:', error);
      toast.error('Failed to fetch portal configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedConfig(null);
    setModalMode('add');
    setConfigData({
      operator: 'jio',
      username: '',
      password: '',
      status: 'inactive'
    });
    setShowConfigModal(true);
  };

  const handleEdit = (config) => {
    setSelectedConfig(config);
    setModalMode('edit');
    setConfigData({
      operator: config.operator || 'jio',
      username: config.username,
      password: '', // Don't populate password for security - user needs to re-enter if changing
      status: 'inactive'
    });
    setShowConfigModal(true);
  };

  const handleDelete = async (config) => {
    if (window.confirm(`Are you sure you want to delete ${config.operator} configuration?`)) {
      try {
        const response = await distributorService.deletePortalConfig(config.id);
        if (response.success) {
          toast.success(response.message || 'Portal configuration deleted successfully');
          fetchPortalConfigs(); // Refresh the list
        } else {
          toast.error(response.error || 'Failed to delete portal configuration');
        }
      } catch (error) {
        console.error('Error deleting portal config:', error);
        toast.error('Failed to delete portal configuration');
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      // Prepare data with proper formatting
      const submitData = {
        ...configData
      };

      if (modalMode === 'add') {
        const response = await distributorService.createPortalConfig(submitData);
        if (response.success) {
          toast.success(response.message || 'Portal configuration added successfully');
          fetchPortalConfigs();
          setShowConfigModal(false);
        } else {
          toast.error(response.error || 'Failed to add portal configuration');
        }
      } else {
        // Only send fields that have been modified
        const updateData = {};
        Object.keys(submitData).forEach(key => {
          // Skip password if empty (user didn't change it)
          if (key === 'password' && submitData[key] === '') {
            return;
          }
          if (submitData[key] !== '' && submitData[key] !== null) {
            updateData[key] = submitData[key];
          }
        });
        
        const response = await distributorService.updatePortalConfig(selectedConfig.id, updateData);
        if (response.success) {
          toast.success(response.message || 'Portal configuration updated successfully');
          fetchPortalConfigs();
          setShowConfigModal(false);
        } else {
          toast.error(response.error || 'Failed to update portal configuration');
        }
      }
    } catch (error) {
      console.error('Error submitting portal config:', error);
      toast.error('Failed to save portal configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfigData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTestConnection = async (config) => {
    setSelectedConfig(config);
    setTestResults(null);
    setShowTestModal(true);

    try {
      const response = await distributorService.testPortalLogin(config.operator);

      const results = {
        success: response.success,
        timestamp: response.data?.login_time || new Date().toISOString(),
        message: response.message || response.error,
        hint: response.hint,
        sessionData: response.data?.session_data,
        tests: response.success && response.data?.session_data ? [
          { name: 'Portal Login', status: 'Pass' },
          { name: 'Cookie Updated', status: response.data.cookie_updated ? 'Pass' : 'Fail' },
          { name: 'Has Cookie', status: response.data.session_data.has_cookie ? 'Pass' : 'Fail' },
          { name: 'Has CSRF Token', status: response.data.session_data.has_csrf_token ? 'Pass' : 'Fail' }
        ] : [
          { name: 'Portal Login', status: 'Fail' }
        ]
      };

      setTestResults(results);

      // Update configuration with test results
      setConfigurations(prev => prev.map(c =>
        c.id === config.id ? {
          ...c,
          last_tested: results.timestamp,
          test_status: response.success ? 'Success' : 'Failed',
          status: response.data?.status || c.status
        } : c
      ));

      if (response.success) {
        toast.success(response.message || 'Portal login successful');
        fetchPortalConfigs(); // Refresh to get updated status
      }
    } catch (error) {
      const results = {
        success: false,
        timestamp: new Date().toISOString(),
        message: 'Portal login automation failed',
        tests: [{ name: 'Portal Login', status: 'Fail' }]
      };
      setTestResults(results);
    }
  };

  const handleAutoLogin = (config) => {
    if (!config.auto_login) {
      toast.error('Auto-login is disabled for this portal');
      return;
    }

    toast.info('Initiating auto-login...');
    
    // Simulate auto-login process
    setTimeout(() => {
      const isSuccess = Math.random() > 0.3;
      if (isSuccess) {
        setConfigurations(prev => prev.map(c => 
          c.id === config.id ? {
            ...c,
            last_login: new Date().toISOString()
          } : c
        ));
        toast.success(`Successfully logged into ${config.portal_name}`);
        // In real implementation, this would open the portal or redirect
        window.open(config.login_url, '_blank');
      } else {
        toast.error('Auto-login failed. Please try manual login.');
      }
    }, 2000);
  };

  const getStatusBadge = (status) => {
    return status === 'active' ? 
      <Badge bg="success">Active</Badge> : 
      <Badge bg="danger">Inactive</Badge>;
  };

  const getTestStatusBadge = (status) => {
    if (status === 'Success') return <Badge bg="success">Success</Badge>;
    if (status === 'Failed') return <Badge bg="danger">Failed</Badge>;
    return <Badge bg="secondary">Pending</Badge>;
  };

  const getAuthTypeBadge = (type) => {
    const badgeMap = {
      'basic': { bg: 'info', text: 'Basic Auth' },
      'oauth': { bg: 'primary', text: 'OAuth 2.0' },
      'token': { bg: 'warning', text: 'Token Auth' },
      'custom': { bg: 'secondary', text: 'Custom' }
    };
    const badge = badgeMap[type] || badgeMap['basic'];
    return <Badge bg={badge.bg}>{badge.text}</Badge>;
  };

  return (
    <Container fluid className="py-1">

      <p>We may access the portal using the credentials provided by you solely for the purpose of manually retrieving the required data. We strictly commit that your credentials and data will be kept confidential, used only for the stated purpose, and will not be shared, stored, or misused in any manner.</p>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-door-open me-2"></i>
          Portal Login Configuration
        </h5>
      </div>


      {/* Configurations Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2">Loading portal configurations...</div>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover className="modern-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Operator</th>
                <th>Username</th>
                <th>Password</th>
                <th>Status</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {configurations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center">
                    No portal configurations found
                  </td>
                </tr>
              ) : (
                configurations.map((config) => (
                  <tr key={config.id}>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => handleTestConnection(config)}
                          title="Test Connection"
                        >
                          <i className="fas fa-play"></i>
                        </Button>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => handleEdit(config)}
                          title="Edit Configuration"
                        >
                          <i className="fas fa-edit"></i>
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(config)}
                          title="Delete Configuration"
                        >
                          <i className="fas fa-trash"></i>
                        </Button>
                      </div>
                    </td>
                    <td>
                      <div>
                        <strong className="text-capitalize">{config.operator}</strong>
                        <br />
                        <small className="text-muted">ID: {config.id}</small>
                      </div>
                    </td>
                    <td>
                      <code>{config.username}</code>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <code>{config.password ? '••••••••' : 'Not set'}</code>
                        {config.password && (
                          <Button
                            variant="link"
                            size="sm"
                            className="ms-2 p-0"
                            onClick={() => {
                              navigator.clipboard.writeText(config.password);
                              toast.success('Password copied to clipboard');
                            }}
                            title="Copy password"
                          >
                            <i className="fas fa-copy"></i>
                          </Button>
                        )}
                      </div>
                    </td>
                    <td>{getStatusBadge(config.status)}</td>
                    <td>
                      <small>{new Date(config.created_at).toLocaleDateString()}</small>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}

      {/* Configuration Form Modal */}
      <Modal show={showConfigModal} onHide={() => setShowConfigModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'add' ? 'Add New' : 'Edit'} Portal Configuration
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Alert variant="info">
              <i className="fas fa-info-circle me-2"></i>
              Configure portal login details for automated access to partner portals.
            </Alert>
            
            <Row>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Username *</Form.Label>
                  <Form.Control
                    type="text"
                    value={configData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    placeholder="Enter username"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6} className="mb-3">
                <Form.Group>
                  <Form.Label>Password {modalMode === 'add' ? '*' : ''}</Form.Label>
                  <Form.Control
                    type="password"
                    value={configData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    placeholder={modalMode === 'edit' ? 'Leave blank to keep existing' : 'Enter password'}
                    required={modalMode === 'add'}
                  />
                  {modalMode === 'edit' && (
                    <Form.Text className="text-muted">
                      Leave blank to keep the existing password
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowConfigModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {modalMode === 'add' ? 'Adding...' : 'Updating...'}
                </>
              ) : (
                <>{modalMode === 'add' ? 'Add' : 'Update'} Portal</>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Test Connection Modal */}
      <Modal show={showTestModal} onHide={() => setShowTestModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Test Portal Connection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedConfig && (
            <div>
              <div className="mb-3">
                
                <small className="text-muted">Username: {selectedConfig.username}</small>
              </div>

              {!testResults ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary mb-3" role="status">
                    <span className="visually-hidden">Testing...</span>
                  </div>
                  <div>Testing portal connection...</div>
                  <small className="text-muted">This may take up to a minute</small>
                </div>
              ) : (
                <div>
                  <Alert variant={testResults.success ? 'success' : 'danger'}>
                    <div className="d-flex align-items-center mb-2">
                      <i className={`fas fa-${testResults.success ? 'check-circle' : 'exclamation-triangle'} me-2`}></i>
                      <strong>
                        {testResults.success ? 'Login Successful' : 'Login Failed'}
                      </strong>
                    </div>
                    <div className="mb-2">{testResults.message}</div>
                    {testResults.hint && (
                      <small className="text-muted d-block">
                        <i className="fas fa-info-circle me-1"></i>
                        {testResults.hint}
                      </small>
                    )}
                  </Alert>

                  {testResults.tests && testResults.tests.length > 0 && (
                    <>
                      <h6>Test Results:</h6>
                      <Table size="sm">
                        <tbody>
                          {testResults.tests.map((test, index) => (
                            <tr key={index}>
                              <td>{test.name}</td>
                              <td>
                                <Badge bg={test.status === 'Pass' ? 'success' : 'danger'}>
                                  {test.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowTestModal(false);
            setTestResults(null);
          }}>
            Close
          </Button>
          {testResults && !testResults.success && (
            <Button variant="primary" onClick={() => {
              setTestResults(null);
              handleTestConnection(selectedConfig);
            }}>
              Retry Test
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default PortalLoginConfig;